/**
 * EVERY COLLEAGUE'S GOOGLE PHOTO, WHEREVER THE PORTAL DRAWS THEIR INITIALS.
 *
 * Gerónimo (29 Oct 2026): «que los avatares se vean con su foto de Google — en
 * la barra lateral, en las tareas y en los tickets — y que al pasar el ratón
 * diga el nombre». The server reads Lexidy's Google directory once (e-mail →
 * photo, server/services/google-photos.service.ts); this file matches it to a
 * person by e-mail, roster id or full name, and puts the photo where the
 * initials were. No photo (or one that does not load): the initials stay.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authHeaders } from '@/lib/authHeaders';
import { WORKERS } from '@/data/workers';

export type Who = { email?: string | null; id?: string | null; name?: string | null };

const norm = (s: string | null | undefined) => (s || '').trim().toLowerCase();

/** The e-mail a person is known by: theirs, their roster id's, or their full name's. */
export function emailOf(who: Who): string {
  if (who.email && who.email.includes('@')) return norm(who.email);
  const byId = who.id ? WORKERS.find((w) => w.id === who.id || w.email?.toLowerCase() === norm(who.id)) : undefined;
  if (byId?.email) return norm(byId.email);
  const n = norm(who.name);
  const byName = n ? WORKERS.find((w) => norm(w.name) === n) : undefined;
  return norm(byName?.email);
}

type PhotosAnswer = { photos: Record<string, string>; why: string };

function usePhotosQuery() {
  return useQuery({
    queryKey: ['people-photos'],
    /* Not the browser's copy: one fetched before Google was switched on (or
       before anyone had connected) said «no photos» and was kept for an hour —
       the photos showed in the chat and nowhere else (29 Oct 2026). */
    queryFn: async ({ signal }) => {
      const r = await fetch('/api/google/photos', { headers: await authHeaders(), signal, cache: 'no-cache' });
      if (!r.ok) return { photos: {}, why: `portal ${r.status}` } as PhotosAnswer;
      const d = (await r.json()) as { photos?: Record<string, string>; why?: string };
      return { photos: d.photos || {}, why: d.why || '' } as PhotosAnswer;
    },
    /* An empty answer is asked again a minute later, not six hours later. */
    staleTime: (q) => (Object.keys(q.state.data?.photos || {}).length ? 6 * 3_600_000 : 60_000),
    gcTime: 12 * 3_600_000,
    retry: false,
  });
}

export function usePeoplePhotos(): Record<string, string> {
  return usePhotosQuery().data?.photos || {};
}

/** How many photos came, and — when none did — why (the settings wheel says it). */
export function usePhotosStatus(): { loading: boolean; n: number; why: string } {
  const q = usePhotosQuery();
  return { loading: q.isLoading, n: Object.keys(q.data?.photos || {}).length, why: q.data?.why || '' };
}

/** This person's Google photo, or null. */
export function usePersonPhoto(who: Who): string | null {
  const photos = usePeoplePhotos();
  const email = emailOf(who);
  return (email && photos[email]) || null;
}

/** The photo, cut to its avatar's circle — or `children` (the initials). */
export function PersonFace({ who, children }: { who: Who; children: ReactNode }) {
  const url = usePersonPhoto(who);
  const [broken, setBroken] = useState(false);
  if (!url || broken) return <>{children}</>;
  return <img className="pf-img" src={url} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setBroken(true)} />;
}
